/**
 * @file: AiTrialChip.tsx
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { formatTrialEnd } from '@euler/functions/entitlements/formatTrialEnd';
import {
  TrialCountdown,
  trialCountdown,
} from '@euler/functions/entitlements/trialCountdown';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo } from 'react';
import { View } from 'react-native';

/** Text and background of each countdown's chip. */
const kTone: Record<TrialCountdown['kind'], { text: string; fill: string }> = {
  far: { text: aiColors.primary, fill: tint(aiColors.primary, 0.1) },
  soon: { text: '#B45309', fill: tint(aiColors.amber, 0.16) },
  urgent: { text: aiColors.destructive, fill: tint(aiColors.red, 0.12) },
};

/**
 * A section's trial: its end date while past a week, then `N 天后到期` in
 * amber, and in red from three days.
 */
export const AiTrialChip = memo(({ trialEndsAt }: { trialEndsAt: string }) => {
  const { rpx } = useResponsiveContext();
  const countdown = trialCountdown(trialEndsAt);
  const tone = kTone[countdown.kind];
  return (
    <View
      style={{
        borderRadius: rpx(999),
        paddingHorizontal: rpx(8),
        paddingVertical: rpx(2),
        backgroundColor: tone.fill,
      }}
    >
      <Typography.Text medium size={rpx(10)} color={tone.text}>
        {countdown.kind === 'far'
          ? formatTrialEnd(trialEndsAt)
          : countdown.daysLeft === 0
          ? '今天到期'
          : `${countdown.daysLeft} 天后到期`}
      </Typography.Text>
    </View>
  );
});
